<template>
  <b-modal
    id="s4c-share"
    v-model="open"
    :title="$t('s4c-utilization.share.title')"
    size="lg"
    ok-only
    :ok-title="$t('s4c-utilization.share.close')"
    @show="load"
  >
    <p class="text-muted small">{{ $t('s4c-utilization.share.intro') }}</p>

    <b-alert v-if="error" show variant="danger" class="small">
      {{ error }}
    </b-alert>

    <div v-if="loading" class="text-center py-3">
      <b-spinner small />
    </div>

    <b-alert
      v-else-if="!recipients.length"
      show
      variant="warning"
      class="small"
    >
      {{ $t('s4c-utilization.share.no-recipients') }}
    </b-alert>

    <template v-else>
      <b-form-group :label="$t('s4c-utilization.share.recipient')">
        <b-form-select v-model="selected" :options="recipientOptions" />
      </b-form-group>

      <b-button
        variant="primary"
        size="sm"
        :disabled="!selected || minting"
        @click="mint"
      >
        <b-spinner v-if="minting" small class="mr-1" />
        {{ $t('s4c-utilization.share.create') }}
      </b-button>

      <div v-if="link" class="s4c-share-result mt-3">
        <label class="small text-muted mb-1">
          {{ $t('s4c-utilization.share.link-for', { email: link.email }) }}
        </label>
        <b-input-group size="sm">
          <b-form-input
            :value="link.url"
            readonly
            @focus="$event.target.select()"
          />
          <b-input-group-append>
            <b-button variant="outline-secondary" @click="copy">
              {{
                copied
                  ? $t('s4c-utilization.share.copied')
                  : $t('s4c-utilization.share.copy')
              }}
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <p class="small text-muted mt-2 mb-0">
          {{ $t('s4c-utilization.share.expires', { date: expiresLabel }) }}
        </p>
        <b-alert show variant="warning" class="small mt-2 mb-0">
          {{ $t('s4c-utilization.share.warning') }}
        </b-alert>
      </div>
    </template>
  </b-modal>
</template>

<script>
import Api from '@gid/vue-common/api';

/**
 * Mints a pre-authenticated link to the S4C utilization report for a holding
 * contact.
 *
 * Same contract as the partner map's share dialog, and for the same reason: the
 * link logs its holder in as the chosen contact, so the recipient list comes
 * from the server and contains only contacts confined to a holding profile. The
 * endpoint refuses to mint for anybody else. No free-text address entry — that
 * would be a way to ask the server to hand out a session for an arbitrary
 * mailbox.
 */
export default {
  name: 'S4cUtilizationShare',
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      open: this.value,
      loading: false,
      minting: false,
      error: null,
      recipients: [],
      selected: null,
      link: null,
      copied: false,
    };
  },
  computed: {
    recipientOptions() {
      return this.recipients.map((r) => ({
        value: r.email,
        text: r.name ? `${r.name} — ${r.email}` : r.email,
      }));
    },
    expiresLabel() {
      if (!this.link) return '';
      return this.$moment(this.link.expires_at).format('LLL');
    },
  },
  watch: {
    value(v) {
      this.open = v;
    },
    open(v) {
      this.$emit('input', v);
      if (!v) {
        // Never leave a live credential sitting in a closed modal.
        this.link = null;
        this.copied = false;
      }
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      this.link = null;
      try {
        const { data } = await Api.get(
          '/api/admin/s4c/utilization/share-recipients',
        );
        this.recipients = data.recipients || [];
        this.selected = this.recipients.length
          ? this.recipients[0].email
          : null;
      } catch (e) {
        this.error = e.response?.data?.msg || e.message;
        this.recipients = [];
      } finally {
        this.loading = false;
      }
    },
    async mint() {
      this.minting = true;
      this.error = null;
      this.copied = false;
      try {
        const { data } = await Api.post('/api/admin/s4c/utilization/share', {
          email: this.selected,
        });
        this.link = data;
      } catch (e) {
        this.error = e.response?.data?.msg || e.message;
        this.link = null;
      } finally {
        this.minting = false;
      }
    },
    async copy() {
      try {
        await navigator.clipboard.writeText(this.link.url);
        this.copied = true;
      } catch (e) {
        this.error = this.$t('s4c-utilization.share.copy-failed');
      }
    },
  },
};
</script>

<style scoped>
.s4c-share-result {
  border-top: 1px solid rgba(128, 128, 128, 0.25);
  padding-top: 12px;
}
</style>
